import { cx } from 'class-variance-authority';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { useDecagonContext } from '@/app/contexts/DecagonProvider';
import usePathnameWithoutLocale from '@/app/hooks/usePathnameWithoutLocale';

export interface ChatbotWindowProps {
  readonly className?: string;
  readonly initialMessage?: string | undefined;
  readonly onClose: () => void;
}

const ChatbotWindow = ({
  className,
  onClose,
  initialMessage,
}: ChatbotWindowProps) => {
  const [isDecagonInitialized, setIsDecagonInitialized] =
    useState<boolean>(false);
  const pathname = usePathnameWithoutLocale();
  const locale = useLocale();
  const { isDecagonScriptLoaded } = useDecagonContext();
  const t = useTranslations();
  const chatTitle = t('chatbot_title');
  const inputPlaceholder = t('chatbot_input_placeholder');

  const needsInitializationRef = useRef<boolean>(true);
  useEffect(() => {
    if (isDecagonScriptLoaded && needsInitializationRef.current) {
      window.decagonEmbed?.setEventListener((event) => {
        if (event.eventName === 'DUET_INITIALIZE') {
          setIsDecagonInitialized(true);
        } else if (
          event.eventName === 'DECAGON_MINIMIZE' ||
          event.eventName === 'DECAGON_SURVEY_CLOSED' ||
          event.eventName === 'DECAGON_CLOSE'
        ) {
          onClose();
        }
      });
      window.decagonEmbed?.init();
      needsInitializationRef.current = false;
    }
  }, [isDecagonScriptLoaded, onClose]);

  useEffect(() => {
    if (isDecagonInitialized) {
      window.decagonEmbed?.setMetadata({
        url_slug: pathname,
        locale: locale,
      });
    }
  }, [isDecagonInitialized, pathname, locale]);

  useEffect(() => {
    if (isDecagonInitialized && initialMessage) {
      window.decagonEmbed?.setTriggerMessage(initialMessage);
    }
  }, [isDecagonInitialized, initialMessage]);

  useEffect(() => {
    if (isDecagonInitialized) {
      window.decagonEmbed?.setLocalizationData({
        chatTitle,
        inputPlaceholder,
      });
    }
  }, [isDecagonInitialized, chatTitle, inputPlaceholder]);

  return (
    <div
      id="decagon-embed-container"
      data-cy="chatbot-window"
      className={cx('pointer-events-auto h-full w-full', className)}
    />
  );
};

export default ChatbotWindow;
